import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { AdminTenant, AdminUser } from '../types/admin';
import { adminApi } from '../services/adminApi';
import { useAuth } from './AuthContext';
import { reportAppError } from '../utils/errorReport';

interface AdminContextType {
  tenants: AdminTenant[];
  selectedTenant: AdminTenant | null;
  tenantUsers: AdminUser[];
  loadingTenants: boolean;
  loadingUsers: boolean;
  error: string | null;
  hasAdminAccess: boolean;
  selectTenant: (tenant: AdminTenant) => void;
  refreshTenants: () => Promise<void>;
  refreshTenantUsers: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<AdminTenant | null>(null);
  const [tenantUsers, setTenantUsers] = useState<AdminUser[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user has admin access (Metrica tenant)
  const hasAdminAccess = String(user?.tenant_id) === '1';

  const refreshTenants = useCallback(async () => {
    if (!hasAdminAccess) return;
    
    setLoadingTenants(true);
    setError(null);
    
    try {
      const data = await adminApi.getTenants();
      setTenants(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch tenants';
      setError(message);
      reportAppError('שגיאה בטעינת רשימת הלקוחות', {
        action: 'Load admin tenants',
        error: err,
      });
    } finally {
      setLoadingTenants(false);
    }
  }, [hasAdminAccess]);

  const refreshTenantUsers = useCallback(async () => {
    if (!selectedTenant) return;
    
    setLoadingUsers(true);
    setError(null);
    
    try {
      const data = await adminApi.getTenantUsers(selectedTenant.id);
      setTenantUsers(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch tenant users';
      setError(message);
      reportAppError('שגיאה בטעינת משתמשי הלקוח', {
        action: 'Load admin tenant users',
        error: err,
      });
    } finally {
      setLoadingUsers(false);
    }
  }, [selectedTenant]);

  const selectTenant = (tenant: AdminTenant) => {
    // Optimistic UI: immediately show the selected tenant
    setSelectedTenant(tenant);
    // Don't clear users immediately - let them fade out naturally
    // Users will be replaced when the new data loads
  };

  useEffect(() => {
    if (hasAdminAccess) {
      refreshTenants();
    }
  }, [hasAdminAccess, refreshTenants]);

  useEffect(() => {
    if (selectedTenant) {
      refreshTenantUsers();
    }
  }, [selectedTenant, refreshTenantUsers]);

  const value: AdminContextType = {
    tenants,
    selectedTenant,
    tenantUsers,
    loadingTenants,
    loadingUsers,
    error,
    hasAdminAccess,
    selectTenant,
    refreshTenants,
    refreshTenantUsers,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
